import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, TextField, CircularProgress, Alert } from '@mui/material';
import EditableTable from '../components/common/EditableTable';
import {
  rawMaterialsAPI, processStagesAPI, utilitiesAPI, packagingSpecsAPI, equipmentsAPI,
  engineeringAPI, configAPI,
} from '../services/api';

const rawMaterialColumns = [
  { field: 'name', header: 'Materia Prima', width: '18%' },
  { field: 'concentration', header: 'Concentracion' },
  { field: 'unit', header: 'Ud.' },
  { field: 'quantity_per_batch', header: 'Cant./Lote', type: 'number' },
  { field: 'supplier', header: 'Proveedor' },
  { field: 'cost_per_unit', header: 'Coste/Ud', type: 'number' },
  { field: 'storage_conditions', header: 'Almacenamiento' },
  { field: 'hazard_class', header: 'Peligrosidad' },
];

const rawMaterialFields = [
  { field: 'name', label: 'Nombre materia prima', required: true },
  { field: 'cas_number', label: 'Numero CAS' },
  { field: 'concentration', label: 'Concentracion / Pureza' },
  { field: 'unit', label: 'Unidad (kg, L, etc.)' },
  { field: 'quantity_per_batch', label: 'Cantidad por lote', type: 'number' },
  { field: 'supplier', label: 'Proveedor' },
  { field: 'cost_per_unit', label: 'Coste por unidad (EUR)', type: 'number' },
  { field: 'storage_conditions', label: 'Condiciones almacenamiento' },
  { field: 'hazard_class', label: 'Clasificacion peligrosidad' },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

const processStageColumns = [
  { field: 'name', header: 'Etapa', width: '20%' },
  { field: 'stage_type', header: 'Tipo' },
  { field: 'duration_minutes', header: 'Duracion (min)', type: 'number' },
  { field: 'temperature', header: 'Temperatura' },
  { field: 'critical_parameters', header: 'Parametros criticos' },
];

const processStageFields = [
  { field: 'name', label: 'Nombre de la etapa', required: true },
  { field: 'stage_type', label: 'Tipo de etapa', type: 'select', options: [
    { value: 'recepcion', label: 'Recepcion' }, { value: 'dosificacion', label: 'Dosificacion' },
    { value: 'mezclado', label: 'Mezclado' }, { value: 'calentamiento', label: 'Calentamiento' },
    { value: 'enfriamiento', label: 'Enfriamiento' }, { value: 'homogeneizacion', label: 'Homogeneizacion' },
    { value: 'filtracion', label: 'Filtracion' }, { value: 'control', label: 'Control calidad' },
    { value: 'envasado', label: 'Envasado' }, { value: 'almacenamiento', label: 'Almacenamiento' },
  ]},
  { field: 'duration_minutes', label: 'Duracion (minutos)', type: 'number' },
  { field: 'temperature', label: 'Temperatura de trabajo' },
  { field: 'pressure', label: 'Presion de trabajo' },
  { field: 'agitation_speed', label: 'Velocidad agitacion' },
  { field: 'input_materials', label: 'Materiales de entrada', fullWidth: true },
  { field: 'output_product', label: 'Producto de salida', fullWidth: true },
  { field: 'critical_parameters', label: 'Parametros criticos de control', multiline: true, rows: 2, fullWidth: true },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

const utilityColumns = [
  { field: 'name', header: 'Servicio', width: '18%' },
  { field: 'utility_type', header: 'Tipo' },
  { field: 'capacity_required', header: 'Capacidad' },
  { field: 'unit', header: 'Unidad' },
  { field: 'pressure', header: 'Presion' },
  { field: 'connection_point', header: 'Punto de acometida' },
];

const utilityFields = [
  { field: 'name', label: 'Nombre del servicio', required: true },
  { field: 'utility_type', label: 'Tipo', type: 'select', options: [
    { value: 'electrical', label: 'Electricidad' }, { value: 'water', label: 'Agua' },
    { value: 'steam', label: 'Vapor' }, { value: 'compressed_air', label: 'Aire comprimido' },
    { value: 'cooling', label: 'Agua de refrigeracion' }, { value: 'nitrogen', label: 'Nitrogeno' },
    { value: 'hot_water', label: 'Agua caliente' }, { value: 'wastewater', label: 'Aguas residuales' },
  ]},
  { field: 'capacity_required', label: 'Capacidad requerida' },
  { field: 'unit', label: 'Unidad (kW, m3/h, kg/h, Nm3/h)' },
  { field: 'pressure', label: 'Presion' },
  { field: 'temperature', label: 'Temperatura' },
  { field: 'connection_point', label: 'Punto de acometida', fullWidth: true },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

const packagingColumns = [
  { field: 'format_name', header: 'Formato', width: '18%' },
  { field: 'container_type', header: 'Envase' },
  { field: 'volume', header: 'Volumen' },
  { field: 'material', header: 'Material' },
  { field: 'closure_type', header: 'Cierre' },
  { field: 'production_speed', header: 'Velocidad' },
];

const defaultContainerOptions = [
  { value: 'botella', label: 'Botella' }, { value: 'garrafa', label: 'Garrafa' },
  { value: 'bidon', label: 'Bidon' }, { value: 'IBC', label: 'IBC' },
  { value: 'bag_in_box', label: 'Bag-in-box' }, { value: 'doypack', label: 'Doypack' },
  { value: 'tubo', label: 'Tubo' }, { value: 'tarro', label: 'Tarro' },
];

function getPackagingFields(containerOptions) {
  return [
    { field: 'format_name', label: 'Nombre del formato', required: true },
    { field: 'container_type', label: 'Tipo envase', type: 'select', options: containerOptions },
    { field: 'volume', label: 'Volumen' },
    { field: 'material', label: 'Material (PET, HDPE, vidrio...)' },
    { field: 'closure_type', label: 'Tipo de cierre' },
    { field: 'label_type', label: 'Tipo de etiqueta' },
    { field: 'production_speed', label: 'Velocidad produccion (env/h)' },
    { field: 'packing', label: 'Agrupacion (pack, caja, palet)', fullWidth: true },
    { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
  ];
}

// En ingenieria conceptual mostramos vista simplificada de equipos (name, capacity, notes)
const conceptualEquipColumns = [
  { field: 'name', header: 'Tipo de maquina / equipo', width: '30%' },
  { field: 'capacity', header: 'Velocidad / Capacidad' },
  { field: 'notes', header: 'Comentarios' },
];

const conceptualEquipFields = [
  { field: 'name', label: 'Tipo de maquina / equipo', required: true },
  { field: 'capacity', label: 'Velocidad / Capacidad' },
  { field: 'notes', label: 'Comentarios', multiline: true, rows: 2, fullWidth: true },
];

const EMPTY_FORM = {
  process_description: '', production_rate: '', batch_size: '',
  estimated_area: '', notes: '',
};

export default function ConceptualEngineering({ projectId, projectType }) {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [processStages, setProcessStages] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [packaging, setPackaging] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [containerOptions, setContainerOptions] = useState(defaultContainerOptions);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [conceptualId, setConceptualId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectId) loadAll();
  }, [projectId]);

  useEffect(() => {
    configAPI.listContainerTypes().then((resp) => {
      if (resp.data.length > 0) {
        setContainerOptions(resp.data.map((ct) => ({ value: ct.value, label: ct.label })));
      }
    }).catch(() => {});
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [rmResp, psResp, utResp, pkResp, eqResp] = await Promise.all([
        rawMaterialsAPI.list(projectId),
        processStagesAPI.list(projectId),
        utilitiesAPI.list(projectId),
        packagingSpecsAPI.list(projectId),
        equipmentsAPI.list(projectId),
      ]);
      setRawMaterials(rmResp.data);
      setProcessStages(psResp.data);
      setUtilities(utResp.data);
      setPackaging(pkResp.data);
      setEquipments(eqResp.data);

      // Load or create conceptual engineering record
      try {
        const cResp = await engineeringAPI.getConceptual(projectId);
        setConceptualId(cResp.data.id);
        setFormData({
          process_description: cResp.data.process_description || '',
          production_rate: cResp.data.production_rate || '',
          batch_size: cResp.data.batch_size || '',
          estimated_area: cResp.data.estimated_area || '',
          notes: cResp.data.notes || '',
        });
      } catch (e) {
        // 404 = not created yet, create it
        const cResp = await engineeringAPI.createConceptual({ project_id: projectId });
        setConceptualId(cResp.data.id);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading conceptual data:', err);
      setError('Error al cargar datos de ingenieria conceptual.');
    } finally {
      setLoading(false);
    }
  };

  const formRef = React.useRef(formData);
  React.useEffect(() => { formRef.current = formData; }, [formData]);

  const handleSave = async () => {
    const data = formRef.current;
    try {
      setSaving(true);
      const payload = {
        project_id: projectId,
        process_description: data.process_description || null,
        production_rate: data.production_rate || null,
        batch_size: data.batch_size || null,
        estimated_area: data.estimated_area ? parseFloat(data.estimated_area) : null,
        notes: data.notes || null,
      };
      await engineeringAPI.updateConceptual(projectId, payload);
    } catch (err) {
      console.error('Error saving:', err);
      setError('Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const makeApiHandlers = (apiModule, loadFn) => ({
    onAdd: async (data) => {
      try { await apiModule.create(projectId, data); await loadFn(); }
      catch (err) { console.error('Error creating:', err); }
    },
    onUpdate: async (id, data) => {
      try { await apiModule.update(id, data); await loadFn(); }
      catch (err) { console.error('Error updating:', err); }
    },
    onDelete: async (id) => {
      try { await apiModule.delete(id); await loadFn(); }
      catch (err) { console.error('Error deleting:', err); }
    },
  });

  const loadRawMaterials = async () => { const r = await rawMaterialsAPI.list(projectId); setRawMaterials(r.data); };
  const loadProcessStages = async () => { const r = await processStagesAPI.list(projectId); setProcessStages(r.data); };
  const loadUtilities = async () => { const r = await utilitiesAPI.list(projectId); setUtilities(r.data); };
  const loadPackaging = async () => { const r = await packagingSpecsAPI.list(projectId); setPackaging(r.data); };
  const loadEquipments = async () => { const r = await equipmentsAPI.list(projectId); setEquipments(r.data); };

  if (!projectId) return <Alert severity="warning">No se ha seleccionado un proyecto.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 3, mt: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Descripcion General del Proceso</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Descripcion del proceso productivo"
              name="process_description" value={formData.process_description} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Ritmo de produccion" placeholder="ej: 2000 L/h"
              name="production_rate" value={formData.production_rate} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Tamano de lote" placeholder="ej: 5000 L"
              name="batch_size" value={formData.batch_size} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Area estimada (m2)" type="number"
              name="estimated_area" value={formData.estimated_area} onChange={handleChange} onBlur={handleSave} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={6} label="Notas generales"
              name="notes" value={formData.notes} onChange={handleChange} onBlur={handleSave} />
          </Grid>
        </Grid>
      </Paper>

      <EditableTable
        title="Materias Primas"
        columns={rawMaterialColumns} rows={rawMaterials} dialogFields={rawMaterialFields}
        emptyMessage="No hay materias primas definidas. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeApiHandlers(rawMaterialsAPI, loadRawMaterials)}
      />

      <EditableTable
        title="Etapas del Proceso"
        columns={processStageColumns} rows={processStages} dialogFields={processStageFields}
        emptyMessage="No hay etapas definidas. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeApiHandlers(processStagesAPI, loadProcessStages)}
      />

      <EditableTable
        title="Utilities / Servicios Auxiliares"
        columns={utilityColumns} rows={utilities} dialogFields={utilityFields}
        emptyMessage="No hay utilities definidos. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeApiHandlers(utilitiesAPI, loadUtilities)}
      />

      <EditableTable
        title="Formatos de Envasado"
        columns={packagingColumns} rows={packaging} dialogFields={getPackagingFields(containerOptions)}
        emptyMessage="No hay formatos de envasado definidos. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeApiHandlers(packagingSpecsAPI, loadPackaging)}
      />

      <EditableTable
        title="Listado de Maquinas"
        columns={conceptualEquipColumns} rows={equipments} dialogFields={conceptualEquipFields}
        notesField="notes"
        emptyMessage="No hay maquinas definidas. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeApiHandlers(equipmentsAPI, loadEquipments)}
      />
    </Box>
  );
}
